import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Card, message, Modal, Switch } from 'antd';
import { hasFeature } from '../../../utils/restrictions';
import { showUpselling } from '../../../store/actions/paymentActions';
import CodeImage from '../../../assets/images/code.png';
import GSheetImage from '../../../assets/images/gsheet3.png';
import WebhookImage from '../../../assets/images/webhooks.png';
import GoogleAnalyticsImage from '../../../assets/images/google-analytics.png';
import PixelAnalyticsImage from '../../../assets/images/meta.png';
import './style.less';

import Icon from '@ant-design/icons';

import { editFormSettings } from '../../../store/actions/formActions';

import WebhookModal from './WebhookModal';
import GoogleSheetModal from './GoogleSheetModal';
import CustomScriptModal from './CustomScriptModal';
import GoogleAnalyticsModal from './GoogleAnalyticsModal';
import PixelModal from './PixelModal';

import {
  validateCustomScriptIntegration,
  validateGoogleAnalyticsIntegration,
  validateGsheetIntegration,
  validatePixelAnalyticsIntegration,
  validateWebhookIntegration,
} from './helpers';
import nProgress from 'nprogress';
import { ExternalLink } from 'react-feather';

const IntegrationSettings = (props) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [activeIntegrationIndex, setActiveIntegrationIndex] = useState();

  const { t } = useTranslation();

  const integrations = [
    {
      key: 'gsheet',
      title: 'Google Sheet',
      desc: t('formSettings.gSheetDesc'),
      icon: GSheetImage,
      component: <GoogleSheetModal form={props.form} />,
      validator: validateGsheetIntegration,
      content: props.form.integrations?.gsheet,
    },
    {
      key: 'customScript',
      title: 'Custom Script',
      desc: t('forsSettings.customScriptDesc'),
      icon: CodeImage,
      component: <CustomScriptModal form={props.form} />,
      validator: validateCustomScriptIntegration,
      content: props.form.integrations?.customScript,
    },
    {
      key: 'webhook',
      title: 'Webhook',
      desc: t('formSettings.webhookDesc'),
      icon: WebhookImage,
      component: <WebhookModal form={props.form} />,
      validator: validateWebhookIntegration,
      content: props.form.integrations?.webhook,
    },
    {
      title: 'Google Analytics',
      desc: t('formSettings.googleAnalyticsDesc'),
      icon: GoogleAnalyticsImage,
      component: <GoogleAnalyticsModal form={props.form} />,
      validator: validateGoogleAnalyticsIntegration,
      content: props.form.integrations?.googleAnalytics,
    },
    {
      title: 'Meta Pixel Analytics',
      desc: t('formSettings.pixelAnalyticsDesc'),
      icon: PixelAnalyticsImage,
      component: <PixelModal form={props.form} />,
      validator: validatePixelAnalyticsIntegration,
      content: props.form.integrations?.pixelAnalytics,
    },
  ];

  const activeIntegration =
    activeIntegrationIndex !== null ? integrations[activeIntegrationIndex] : null;

  return (
    <div className="form-settings-screen">
      {activeIntegration ? (
        <IntegrationModal
          title={activeIntegration?.title}
          isModalOpen={isModalOpen}
          setModalOpen={setModalOpen}
        >
          {activeIntegration.component}
        </IntegrationModal>
      ) : (
        <></>
      )}

      <section className="integration-cards-section">
        {integrations.map((integration, i) => (
          <IntegrationCard
            key={integration.title}
            integration={integration}
            plan={props.plan}
            setModalOpen={setModalOpen}
            setActiveIntegrationIndex={setActiveIntegrationIndex}
            index={i}
            form={props.form}
          />
        ))}
      </section>
    </div>
  );
};

const IntegrationCard = ({
  integration,
  plan,
  setActiveIntegrationIndex,
  setModalOpen,
  index,
  form,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const [isActive, setIsActive] = useState(false);

  /**
   * Sync state with external store
   */
  useEffect(() => {
    const checkIntegrationActive =
      integration.validator(integration.content) && integration.content.isActive !== false;
    setIsActive(checkIntegrationActive);
  }, [integration]);

  async function handleActiveStatusChange(value) {
    if (!hasFeature(plan, 'integrations')) {
      return dispatch(showUpselling('integrations'));
    }

    try {
      // Immediate state change
      setIsActive(value);
      nProgress.start();
      const event = await dispatch(
        editFormSettings(form.id, {
          whatsappNumber: form.whatsappNumber, // To jump validation
          integrations: {
            ...form.integrations,
            [integration.key]: { ...integration.content, isActive: value },
          },
        }),
      );
      if (event && event.type === 'EDIT_FORM_FAIL') {
        throw Error();
      }
    } catch (e) {
      console.log(e);
      message.error('Sorry, could not change status of integration');
    } finally {
      nProgress.done();
    }
  }

  function handleOpenIntegration() {
    if (!hasFeature(plan, 'integrations')) {
      dispatch(showUpselling('integrations'));
    } else {
      setActiveIntegrationIndex(index);
      setModalOpen(true);
    }
  }

  const buttons = integration.validator(integration.content)
    ? [
        <div className="connected-integration-buttons" key={0}>
          <Button
            disabled={!integration.validator(integration.content)}
            key={0}
            onClick={handleOpenIntegration}
          >
            {t('formSettings.viewIntegration')}
          </Button>
          <Switch key={1} checked={isActive} onClick={handleActiveStatusChange} loading={loading} />
        </div>,
      ]
    : [
        <Button key={0} onClick={handleOpenIntegration}>
          {t('formSettings.connect')}
        </Button>,
      ];

  return (
    <Card
      style={{
        width: 300,
        minHeight: 225,
      }}
    >
      <section className="integration-info-section">
        <div className="integration-header">
          <div>
            {' '}
            <div className="integration-title">{integration.title}</div>
            <div>
              {/*  <a>
                Learn more{' '}
                <Icon>
                  <ExternalLink size="1rem" />
                </Icon>
              </a> */}
            </div>
          </div>
          <Avatar size="large" src={integration.icon} />
        </div>
        <div className="integration-desc">{integration.desc}</div>
      </section>
      <section className="integration-action-section">{buttons}</section>
    </Card>
  );
};

const IntegrationModal = ({ isModalOpen, setModalOpen, title = '', children }) => {
  return (
    <Modal
      title={title}
      visible={isModalOpen}
      onCancel={() => setModalOpen(false)}
      footer={false}
      width={800}
      destroyOnClose={true}
      className="integration-modal"
      centered={true}
    >
      {children}
    </Modal>
  );
};

export default IntegrationSettings;
